import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Button from '../../../../components/ui/Button';
import { API_ENDPOINTS } from '../../../../utils/config';
import { FileText, Search, AlertCircle, CheckCircle, Eye, Edit, Trash2, X, Calendar } from 'lucide-react';

const SalesRegister = () => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterReceived, setFilterReceived] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [ordering, setOrdering] = useState('-invoice_date');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [selected, setSelected] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isCreateLoading, setIsCreateLoading] = useState(false);

  const blankForm = {
    invoice: '',
    division: '',
    receiver_gstin: '',
    receiver_name: '',
    tax_invoice_no: '',
    invoice_date: '',
    igst: '',
    cgst: '',
    sgst: '',
    total_invoice_value: '',
    tds: '',
    bank_receipt: '',
    received_in_bank: false,
    date_of_receipt: '',
    remarks: ''
  };

  const [createForm, setCreateForm] = useState({ ...blankForm });
  const [editForm, setEditForm] = useState({ ...blankForm });

  const getErrorMessage = (err, fallback) => {
    const respData = err?.response?.data;
    const message =
      respData?.message ||
      respData?.detail ||
      respData?.error ||
      (typeof respData === 'string' ? respData : null) ||
      err?.message ||
      fallback;
    if (typeof message === 'string') return message;
    try {
      return JSON.stringify(message || fallback);
    } catch {
      return fallback;
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  const fetchItems = async (page = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterReceived !== 'all') params.append('received_in_bank', filterReceived);
      if (filterMonth !== 'all') params.append('month', filterMonth);
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', page);
      params.append('ordering', ordering || '-invoice_date');

      const response = await axios.get(`${API_ENDPOINTS.SALES_REGISTER_LIST}?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      const data = response.data;
      const normalized = Array.isArray(data) ? data : data.results || [];
      const normalizedCount = Array.isArray(data) ? data.length : data.count || normalized.length || 0;

      setItems(normalized);
      setTotalCount(normalizedCount);
      setNextPage(Array.isArray(data) ? null : data.next);
      setPrevPage(Array.isArray(data) ? null : data.previous);
      setCurrentPage(page);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch sales register entries'));
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterReceived, filterMonth, ordering]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchItems(1), 400);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const formatCurrency = (value) =>
    `₹${(parseFloat(value || 0) || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

  const openCreate = () => {
    setCreateForm({
      ...blankForm,
      invoice_date: new Date().toISOString().split('T')[0],
      date_of_receipt: ''
    });
    setIsCreateOpen(true);
    setError('');
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    setIsCreateLoading(true);
    try {
      const payload = {
        invoice: createForm.invoice || null,
        division: createForm.division || '',
        receiver_gstin: createForm.receiver_gstin || '',
        receiver_name: createForm.receiver_name || '',
        tax_invoice_no: createForm.tax_invoice_no || '',
        invoice_date: createForm.invoice_date || null,
        igst: createForm.igst || '0',
        cgst: createForm.cgst || '0',
        sgst: createForm.sgst || '0',
        total_invoice_value: createForm.total_invoice_value || '0',
        tds: createForm.tds || '0',
        bank_receipt: createForm.bank_receipt || '0',
        received_in_bank: !!createForm.received_in_bank,
        date_of_receipt: createForm.date_of_receipt || null,
        remarks: createForm.remarks || ''
      };
      const response = await axios.post(API_ENDPOINTS.SALES_REGISTER_LIST, payload, { headers: getAuthHeaders() });
      const msg = response?.data?.message || 'Sales register entry created successfully';
      setSuccessMessage(msg);
      setIsCreateOpen(false);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchItems(currentPage);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create sales register entry'));
    } finally {
      setIsCreateLoading(false);
    }
  };

  const handleView = async (row) => {
    try {
      setIsLoading(true);
      const response = await axios.get(API_ENDPOINTS.SALES_REGISTER_DETAIL(row.id), { headers: getAuthHeaders() });
      setSelected(response.data);
      setIsDetailsOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch sales register details'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenEdit = async (row) => {
    try {
      setIsEditLoading(true);
      const response = await axios.get(API_ENDPOINTS.SALES_REGISTER_DETAIL(row.id), { headers: getAuthHeaders() });
      const data = response.data || row;
      setSelected(data);
      setEditForm({
        invoice: data.invoice || '',
        division: data.division || '',
        receiver_gstin: data.receiver_gstin || '',
        receiver_name: data.receiver_name || '',
        tax_invoice_no: data.tax_invoice_no || '',
        invoice_date: data.invoice_date ? data.invoice_date.split('T')[0] : '',
        igst: data.igst || '',
        cgst: data.cgst || '',
        sgst: data.sgst || '',
        total_invoice_value: data.total_invoice_value || '',
        tds: data.tds || '',
        bank_receipt: data.bank_receipt || '',
        received_in_bank: !!data.received_in_bank,
        date_of_receipt: data.date_of_receipt ? data.date_of_receipt.split('T')[0] : '',
        remarks: data.remarks || ''
      });
      setIsEditOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load sales register for editing'));
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!selected?.id) return;
    setIsEditLoading(true);
    try {
      const payload = {
        invoice: editForm.invoice || null,
        division: editForm.division || '',
        receiver_gstin: editForm.receiver_gstin || '',
        receiver_name: editForm.receiver_name || '',
        tax_invoice_no: editForm.tax_invoice_no || '',
        invoice_date: editForm.invoice_date || null,
        igst: editForm.igst || '0',
        cgst: editForm.cgst || '0',
        sgst: editForm.sgst || '0',
        total_invoice_value: editForm.total_invoice_value || '0',
        tds: editForm.tds || '0',
        bank_receipt: editForm.bank_receipt || '0',
        received_in_bank: !!editForm.received_in_bank,
        date_of_receipt: editForm.date_of_receipt || null,
        remarks: editForm.remarks || ''
      };
      const response = await axios.put(
        API_ENDPOINTS.SALES_REGISTER_UPDATE(selected.id),
        payload,
        { headers: getAuthHeaders() }
      );
      const msg = response?.data?.message || 'Sales register entry updated successfully';
      setSuccessMessage(msg);
      setIsEditOpen(false);
      setSelected(null);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchItems(currentPage);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update sales register entry'));
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleOpenDelete = (row) => {
    setDeleteTarget(row);
    setIsDeleteOpen(true);
    setError('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleteLoading(true);
    try {
      const response = await axios.delete(API_ENDPOINTS.SALES_REGISTER_DELETE(deleteTarget.id), {
        headers: getAuthHeaders()
      });
      const msg = response?.data?.message || 'Sales register entry deleted successfully';
      setSuccessMessage(msg);
      setIsDeleteOpen(false);
      setDeleteTarget(null);
      fetchItems(currentPage);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete sales register entry'));
    } finally {
      setIsDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span>{successMessage}</span>
          </div>
          <button onClick={() => setSuccessMessage('')} className="text-green-600 hover:text-green-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Summary card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-muted-foreground">Total Entries</p>
          <p className="text-2xl font-bold text-foreground">{totalCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search sales register..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <select
            value={filterReceived}
            onChange={(e) => setFilterReceived(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">Received in Bank?</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
          <input
            type="month"
            value={filterMonth === 'all' ? '' : filterMonth}
            onChange={(e) => setFilterMonth(e.target.value || 'all')}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <select
            value={ordering}
            onChange={(e) => setOrdering(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="-invoice_date">Newest</option>
            <option value="invoice_date">Oldest</option>
            <option value="-total_invoice_value">Invoice Value: High to Low</option>
            <option value="total_invoice_value">Invoice Value: Low to High</option>
          </select>
          <Button variant="outline" onClick={openCreate}>
            <FileText className="w-4 h-4 mr-2" />
            New Entry
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading sales register...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-muted-foreground">No sales register entries found</p>
            </div>
          ) : (
            <>
              <table className="w-full min-w-[1100px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receiver</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice Value</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Receivable</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Received in Bank</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">{row.tax_invoice_no || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{row.receiver_name || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        {row.invoice_date ? new Date(row.invoice_date).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-foreground">
                        {formatCurrency(row.total_invoice_value)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-foreground">
                        {formatCurrency(row.net_receivable)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        {row.received_in_bank ? 'Yes' : 'No'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Button variant="ghost" size="sm" title="View" onClick={() => handleView(row)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" title="Edit" onClick={() => handleOpenEdit(row)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" title="Delete" onClick={() => handleOpenDelete(row)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {(nextPage || prevPage) && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">Showing page {currentPage} of {Math.ceil(totalCount / 10)}</div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={() => fetchItems(currentPage - 1)} disabled={!prevPage}>
                      Previous
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => fetchItems(currentPage + 1)} disabled={!nextPage}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {isDetailsOpen && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Sales Register Details</h3>
                <p className="text-sm text-muted-foreground">Invoice #{selected.tax_invoice_no || 'N/A'}</p>
              </div>
              <Button variant="outline" onClick={() => { setIsDetailsOpen(false); setSelected(null); }}>
                Close
              </Button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><p className="text-muted-foreground">Division</p><p className="font-semibold">{selected.division || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Receiver Name</p><p className="font-semibold">{selected.receiver_name || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Receiver GSTIN</p><p className="font-semibold">{selected.receiver_gstin || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Invoice Date</p><p className="font-semibold">{selected.invoice_date ? new Date(selected.invoice_date).toLocaleDateString() : 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Total Invoice Value</p><p className="font-semibold">{formatCurrency(selected.total_invoice_value)}</p></div>
                <div><p className="text-muted-foreground">Net Receivable</p><p className="font-semibold">{formatCurrency(selected.net_receivable)}</p></div>
                <div><p className="text-muted-foreground">Bank Receipt</p><p className="font-semibold">{formatCurrency(selected.bank_receipt)}</p></div>
                <div><p className="text-muted-foreground">Received in Bank</p><p className="font-semibold">{selected.received_in_bank ? 'Yes' : 'No'}</p></div>
                <div><p className="text-muted-foreground">Date of Receipt</p><p className="font-semibold">{selected.date_of_receipt ? new Date(selected.date_of_receipt).toLocaleDateString() : 'N/A'}</p></div>
                <div><p className="text-muted-foreground">IGST</p><p className="font-semibold">{formatCurrency(selected.igst)}</p></div>
                <div><p className="text-muted-foreground">CGST</p><p className="font-semibold">{formatCurrency(selected.cgst)}</p></div>
                <div><p className="text-muted-foreground">SGST</p><p className="font-semibold">{formatCurrency(selected.sgst)}</p></div>
                <div><p className="text-muted-foreground">TDS</p><p className="font-semibold">{formatCurrency(selected.tds)}</p></div>
                <div><p className="text-muted-foreground">Pending Balance</p><p className="font-semibold">{formatCurrency(selected.pending_balance)}</p></div>
                <div><p className="text-muted-foreground">Delay Days</p><p className="font-semibold">{selected.delay_days ?? 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Created</p><p className="font-semibold">{selected.created_at ? new Date(selected.created_at).toLocaleString() : 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Updated</p><p className="font-semibold">{selected.updated_at ? new Date(selected.updated_at).toLocaleString() : 'N/A'}</p></div>
              </div>
              {selected.remarks && (
                <div>
                  <p className="text-sm font-semibold text-foreground">Remarks</p>
                  <p className="text-sm text-muted-foreground bg-gray-50 border border-gray-200 rounded p-3">
                    {selected.remarks}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {isCreateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Create Sales Register Entry</h3>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Close</Button>
            </div>
            <form onSubmit={handleSubmitCreate} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Invoice ID</label>
                  <input type="text" value={createForm.invoice} onChange={(e) => setCreateForm((p) => ({ ...p, invoice: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Division</label>
                  <input type="text" value={createForm.division} onChange={(e) => setCreateForm((p) => ({ ...p, division: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Receiver Name</label>
                  <input type="text" value={createForm.receiver_name} onChange={(e) => setCreateForm((p) => ({ ...p, receiver_name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Receiver GSTIN</label>
                  <input type="text" value={createForm.receiver_gstin} onChange={(e) => setCreateForm((p) => ({ ...p, receiver_gstin: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Tax Invoice No</label>
                  <input type="text" value={createForm.tax_invoice_no} onChange={(e) => setCreateForm((p) => ({ ...p, tax_invoice_no: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                  <input type="date" value={createForm.invoice_date} onChange={(e) => setCreateForm((p) => ({ ...p, invoice_date: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Date of Receipt</label>
                  <input type="date" value={createForm.date_of_receipt} onChange={(e) => setCreateForm((p) => ({ ...p, date_of_receipt: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">IGST</label>
                  <input type="number" step="0.01" value={createForm.igst} onChange={(e) => setCreateForm((p) => ({ ...p, igst: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">CGST</label>
                  <input type="number" step="0.01" value={createForm.cgst} onChange={(e) => setCreateForm((p) => ({ ...p, cgst: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">SGST</label>
                  <input type="number" step="0.01" value={createForm.sgst} onChange={(e) => setCreateForm((p) => ({ ...p, sgst: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Total Invoice Value</label>
                  <input type="number" step="0.01" value={createForm.total_invoice_value} onChange={(e) => setCreateForm((p) => ({ ...p, total_invoice_value: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">TDS</label>
                  <input type="number" step="0.01" value={createForm.tds} onChange={(e) => setCreateForm((p) => ({ ...p, tds: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Bank Receipt</label>
                  <input type="number" step="0.01" value={createForm.bank_receipt} onChange={(e) => setCreateForm((p) => ({ ...p, bank_receipt: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-2 pt-6">
                  <input
                    id="received_in_bank_create"
                    type="checkbox"
                    checked={createForm.received_in_bank}
                    onChange={(e) => setCreateForm((p) => ({ ...p, received_in_bank: e.target.checked }))}
                    className="h-4 w-4 text-primary border-gray-300 rounded"
                  />
                  <label htmlFor="received_in_bank_create" className="text-sm text-gray-700">Received in Bank</label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  rows={3}
                  value={createForm.remarks}
                  onChange={(e) => setCreateForm((p) => ({ ...p, remarks: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Add remarks"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={isCreateLoading}>{isCreateLoading ? 'Creating...' : 'Create'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditOpen && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Update Sales Register Entry</h3>
              <Button variant="outline" onClick={() => { setIsEditOpen(false); setSelected(null); }}>Close</Button>
            </div>
            <form onSubmit={handleSubmitEdit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Invoice ID</label>
                  <input type="text" value={editForm.invoice} onChange={(e) => setEditForm((p) => ({ ...p, invoice: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Division</label>
                  <input type="text" value={editForm.division} onChange={(e) => setEditForm((p) => ({ ...p, division: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Receiver Name</label>
                  <input type="text" value={editForm.receiver_name} onChange={(e) => setEditForm((p) => ({ ...p, receiver_name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Receiver GSTIN</label>
                  <input type="text" value={editForm.receiver_gstin} onChange={(e) => setEditForm((p) => ({ ...p, receiver_gstin: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Tax Invoice No</label>
                  <input type="text" value={editForm.tax_invoice_no} onChange={(e) => setEditForm((p) => ({ ...p, tax_invoice_no: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                  <input type="date" value={editForm.invoice_date} onChange={(e) => setEditForm((p) => ({ ...p, invoice_date: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Date of Receipt</label>
                  <input type="date" value={editForm.date_of_receipt} onChange={(e) => setEditForm((p) => ({ ...p, date_of_receipt: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">IGST</label>
                  <input type="number" step="0.01" value={editForm.igst} onChange={(e) => setEditForm((p) => ({ ...p, igst: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">CGST</label>
                  <input type="number" step="0.01" value={editForm.cgst} onChange={(e) => setEditForm((p) => ({ ...p, cgst: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">SGST</label>
                  <input type="number" step="0.01" value={editForm.sgst} onChange={(e) => setEditForm((p) => ({ ...p, sgst: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Total Invoice Value</label>
                  <input type="number" step="0.01" value={editForm.total_invoice_value} onChange={(e) => setEditForm((p) => ({ ...p, total_invoice_value: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">TDS</label>
                  <input type="number" step="0.01" value={editForm.tds} onChange={(e) => setEditForm((p) => ({ ...p, tds: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Bank Receipt</label>
                  <input type="number" step="0.01" value={editForm.bank_receipt} onChange={(e) => setEditForm((p) => ({ ...p, bank_receipt: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" /></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-2 pt-6">
                  <input
                    id="received_in_bank_edit"
                    type="checkbox"
                    checked={editForm.received_in_bank}
                    onChange={(e) => setEditForm((p) => ({ ...p, received_in_bank: e.target.checked }))}
                    className="h-4 w-4 text-primary border-gray-300 rounded"
                  />
                  <label htmlFor="received_in_bank_edit" className="text-sm text-gray-700">Received in Bank</label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  rows={3}
                  value={editForm.remarks}
                  onChange={(e) => setEditForm((p) => ({ ...p, remarks: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Update remarks"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => { setIsEditOpen(false); setSelected(null); }}>Cancel</Button>
                <Button type="submit" disabled={isEditLoading}>{isEditLoading ? 'Updating...' : 'Update'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {isDeleteOpen && deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-foreground">Delete Sales Register Entry</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Are you sure you want to delete{' '}
                <span className="font-semibold">{deleteTarget.tax_invoice_no || `#${deleteTarget.id}`}</span>?
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setIsDeleteOpen(false); setDeleteTarget(null); }} disabled={isDeleteLoading}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleteLoading}>
                {isDeleteLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesRegister;

